/**
 * Was ist passiert, und gibt es etwas zu entscheiden?
 *
 * Die Fachlogik dahinter steht in `docs/ARBEITSABLAUF.md`. Sie gehört aber an
 * den Ort der Entscheidung, nicht in ein Dokument, das beim Arbeiten zu ist.
 * Deshalb hier: je Session der Hergang in Alltagssprache und die Knöpfe.
 *
 * Formuliert wird in Ereignissen („niemand hat das Terminal ausgelesen“), nicht
 * in Modellbegriffen („ein Report deckt mehrere Abschlüsse ab“). Wer zum ersten
 * Mal davorsitzt, kennt das Modell nicht.
 *
 * Zwei Regeln aus dem ersten Testlauf mit der Buchhaltung (Oktober 2026):
 *
 * - **Was stimmt, braucht keinen Klick.** Knöpfe gibt es nur bei einem offenen
 *   roten Hinweis. Ein „Bestätigen“ an einer unauffälligen Kasse fragt nach
 *   einer Entscheidung, die es nicht gibt, und wird nach dem dritten Mal
 *   ungelesen geklickt — dann auch dort, wo es zählt.
 * - **Ein Knopf sagt, was man feststellt.** „Betrag stimmt“ oder „Aufteilung
 *   übernehmen“, nie nur „Bestätigen“: so steht es danach im Audit-Log, und so
 *   weiß man beim Klicken, wofür man unterschreibt.
 *
 * Gerechnet wird hier nicht. Alle Beträge kommen fertig aus der API.
 */
import type { CashSessionDto } from "./api";
import { euro, shortDate } from "./format";
import { isBlockingFlag } from "../components/ui";

/** Was ein Knopf am Ende auslöst. */
export type ActionKind =
  /** Feststellung: so stimmt es. Der Knopf trägt den Satz, nicht dieses Wort. */
  | "confirm"
  | "noTip"
  /** Öffnet die fehlende Zeile der Rechnung zum Eintragen. */
  | "counterpart"
  /** Öffnet die Rechnung zum Korrigieren. */
  | "amounts"
  /** Zieht den Übertrag aus einem vergessenen Kassenschnitt ab, ein Klick. */
  | "uebertrag"
  | "tipOverride"
  | "actsAs"
  | "date";

export interface Entscheidung {
  aktion: ActionKind;
  /** Was man mit dem Klick feststellt. */
  label: string;
}

export interface Advice {
  /** Der Hinweis, der den Fall bestimmt. */
  leitHinweis: string | null;
  /** Was passiert ist. Ein bis drei Sätze, ohne Fachbegriffe. */
  hergang: string[];
  /** Nur ein offener roter Hinweis verlangt eine Entscheidung. */
  zuEntscheiden: boolean;
  /** Die Knöpfe, der empfohlene zuerst. Leer, wenn nichts zu entscheiden ist. */
  entscheidungen: Entscheidung[];
  /** Die empfohlene Aktion, kurz: die erste Entscheidung. */
  empfehlung: ActionKind | null;
  /** Steht über den Knöpfen: worauf es bei der Entscheidung ankommt. */
  warum: string | null;
  /** Wann etwas anderes richtig wäre. */
  sonst: string | null;
  /** Statt eines Knopfs: ein Satz, wenn es nichts zu entscheiden gibt. */
  ruhig: string | null;
}

/**
 * Reihenfolge der Dringlichkeit. Der erste zutreffende Hinweis bestimmt die
 * Erzählung; die übrigen stehen als Chips daneben.
 */
const RANG = [
  "UNKNOWN_WORKSTATION",
  "CARRYOVER_FROM_COUNTERPART",
  "NEGATIVE_TIP",
  "MISSING_REPORT",
  "MISSING_CLOSING",
  "SPLIT_REPORT",
  "SPLIT_CLOSING",
  "NEGATIVE_REVENUE",
  "HIGH_TIP",
  "IMPLAUSIBLE_TIP",
  "LATE_CLOSING",
  "LATE_REPORT",
  "COUNTERPART_IN_SIBLING",
] as const;

/** Der Fall, ohne Rücksicht darauf, ob er schon entschieden ist. */
interface Fall {
  hergang: string[];
  entscheidungen: Entscheidung[];
  warum: string | null;
  sonst: string | null;
}

const BETRAG_STIMMT: Entscheidung = { aktion: "confirm", label: "Betrag stimmt" };
const WERTE_KORRIGIEREN: Entscheidung = { aktion: "amounts", label: "Werte korrigieren" };
const OHNE_TRINKGELD: Entscheidung = { aktion: "noTip", label: "Ohne Trinkgeld werten" };

export function adviseSession(session: CashSessionDto): Advice {
  const leit = RANG.find((f) => session.flags.includes(f)) ?? null;
  const fall = beschreibe(leit, session);
  const zuEntscheiden =
    session.status === "PROPOSED" && session.flags.some((f) => isBlockingFlag(f));

  if (zuEntscheiden) {
    return {
      leitHinweis: leit,
      hergang: fall.hergang,
      zuEntscheiden,
      entscheidungen: fall.entscheidungen,
      empfehlung: fall.entscheidungen[0]?.aktion ?? null,
      warum: fall.warum,
      sonst: fall.sonst,
      ruhig: null,
    };
  }

  return {
    leitHinweis: leit,
    hergang: fall.hergang,
    zuEntscheiden,
    entscheidungen: [],
    empfehlung: null,
    warum: null,
    // Bei einem entschiedenen Fall ist „was sonst richtig wäre“ erledigt.
    sonst: session.status === "CONFIRMED" ? null : fall.sonst,
    ruhig:
      session.status === "EXCLUDED"
        ? "Diese Kasse zahlt in keinen Topf ein. Nichts zu tun."
        : session.status === "CONFIRMED" && session.flags.some((f) => isBlockingFlag(f))
          ? "Entschieden. Wer wann was festgestellt hat, steht im Audit-Log."
          : leit
            ? "Nur zur Kenntnis. Die Freigabe hängt nicht daran."
            : "Passt: Abschluss und Terminal stimmen überein, das Trinkgeld ist plausibel. Nichts zu tun.",
  };
}

/** „19.09.“, „19.09. und 22.09.“, „19.09., 22.09. und 23.09.“ */
function aufzaehlung(teile: readonly string[]): string {
  if (teile.length <= 1) return teile[0] ?? "";
  return `${teile.slice(0, -1).join(", ")} und ${teile[teile.length - 1]}`;
}

function beschreibe(leit: (typeof RANG)[number] | null, session: CashSessionDto): Fall {
  const geteilt = session.kontext?.geteilterBeleg ?? null;

  switch (leit) {
    case "UNKNOWN_WORKSTATION":
      return {
        hergang: [
          "Diese Kasse ist in den Stammdaten nicht hinterlegt. Wahrscheinlich wurde ein Terminal getauscht oder eine neue Kasse aufgestellt.",
        ],
        entscheidungen: [],
        warum: null,
        sonst:
          "Erst die Kasse unter Stammdaten anlegen, dann oben auf „Neu abgleichen“. Danach ordnet sich die Session von selbst zu.",
      };

    case "CARRYOVER_FROM_COUNTERPART": {
      // Nicht der Vortag, sondern der nächste Einsatz dieser Kasse: Party am
      // 19., nächste Veranstaltung am 22. — dann steckt der 19. im 22.
      const gruppen = session.uebertraege ?? [];
      const von = gruppen.flatMap((g) => g.von).sort((a, b) => a.datum.localeCompare(b.datum));
      const daten = aufzaehlung([...new Set(von.map((v) => shortDate(v.datum)))]);
      const wert =
        gruppen.length === 1 && gruppen[0]?.feld === "zBon"
          ? "Kassenabschluss"
          : "Auslesevorgang am Karten-Terminal";
      const einer = gruppen.length === 1 ? gruppen[0]! : null;
      const vorschlagFertig = gruppen.length > 0 && gruppen.every((g) => g.vorschlag !== null);
      // Passt der Abzug nicht — es bliebe weniger, als die Kasse an Umsatz
      // meldet —, steckt der frühere Abend hier nicht drin. Kein Ein-Klick-Abzug.
      const passt = vorschlagFertig && gruppen.every((g) => g.passt);
      return {
        hergang:
          von.length === 0
            ? [
                "An einem früheren Abend dieser Kasse wurde ein fehlender Wert von Hand erfasst. Wurde dort der Kassenschnitt vergessen, steckt derselbe Umsatz hier noch einmal.",
              ]
            : von.length === 1
              ? [
                  `Am ${daten} fehlte an dieser Kasse ein Wert; ${euro(von[0]!.betrag)} wurden dort von Hand erfasst. Wurde an dem Abend der Kassenschnitt vergessen, ist der Umsatz bis zum nächsten Einsatz der Kasse weitergelaufen und steckt in diesem ${wert} noch einmal.`,
                  "Bleibt er drin, ist das Trinkgeld dieses Abends um genau diesen Betrag zu hoch.",
                ]
              : [
                  `Am ${daten} fehlte an dieser Kasse jeweils ein Wert; er wurde dort von Hand erfasst. Wurde an diesen Abenden der Kassenschnitt vergessen, ist der Umsatz bis zum nächsten Einsatz der Kasse weitergelaufen und steckt in diesem ${wert} noch einmal.`,
                  "Bleiben die Beträge drin, ist das Trinkgeld dieses Abends um genau ihre Summe zu hoch.",
                ],
        entscheidungen: !passt
          ? [{ aktion: "confirm", label: "Nichts doppelt" }, WERTE_KORRIGIEREN]
          : [
              {
                aktion: "uebertrag",
                label: einer ? `${euro(einer.betrag)} vom ${daten} abziehen` : `Überträge vom ${daten} abziehen`,
              },
              { aktion: "confirm", label: "Nichts doppelt" },
            ],
        warum: !passt
          ? `Der Betrag vom ${daten} ist größer, als in diesem Wert Platz hat: abgezogen bliebe weniger, als die Kasse an Umsatz meldet. Dann steckt er hier wohl nicht drin. Im Händler-Report prüfen.`
          : einer
            ? `Abgezogen bleiben ${euro(einer.vorschlag)} für diesen Abend.`
            : `Abgezogen bleiben ${gruppen
                .map((g) => `${euro(g.vorschlag)} ${g.feld === "zBon" ? "laut Kasse" : "am Terminal"}`)
                .join(" und ")}.`,
        sonst: !passt
          ? "„Werte korrigieren“, wenn der Händler-Report für diesen Abend etwas anderes zeigt."
          : "„Nichts doppelt“ nur, wenn damals bloß der Report in der Datei fehlte und der Kassenschnitt gemacht wurde.",
      };
    }

    case "NEGATIVE_REVENUE":
      return {
        hergang: [
          "Kasse oder Terminal melden für diesen Abend einen negativen Umsatz. Das kommt vor, wenn an einer Kasse nur Pfand zurückgezahlt wurde.",
        ],
        entscheidungen: [BETRAG_STIMMT, WERTE_KORRIGIEREN],
        warum: "Passt das zum Abend, etwa eine reine Pfandrückgabe, stimmt der Betrag.",
        sonst: null,
      };

    case "HIGH_TIP":
      return {
        hergang: [
          "Das Trinkgeld dieser Kasse an diesem Abend liegt über der Schwelle aus den Einstellungen. Das kann ein starker Abend sein.",
          "Oft steckt aber ein vergessener Kassenschnitt dahinter: dann ist Umsatz eines anderen Abends mitgezählt und als Trinkgeld verbucht.",
        ],
        entscheidungen: [BETRAG_STIMMT, WERTE_KORRIGIEREN],
        warum: "Im Händler-Report nachsehen, ob der Betrag zu diesem Abend passt.",
        sonst:
          "Fehlte der Kassenschnitt: „Werte korrigieren“ und den Kartenumsatz dieses Abends eintragen.",
      };

    case "NEGATIVE_TIP":
      return {
        hergang: [
          "Am Karten-Terminal ist weniger angekommen, als die Kasse an Kartenumsatz meldet. Gäste geben kein negatives Trinkgeld, also stimmt hier die Zuordnung nicht.",
          "Meist gehören die beiden Belege nicht zusammen, oder ein Betrag wurde falsch übernommen.",
        ],
        entscheidungen: [WERTE_KORRIGIEREN],
        warum: "Die echten Werte eintragen, sobald klar ist, welcher der beiden falsch war.",
        sonst:
          "Stand die Kasse an dem Abend woanders, unten „anderer Bereich“ wählen. Der Umsatz gehört dann in einen anderen Topf.",
      };

    case "MISSING_REPORT":
      return {
        hergang: [
          "Die Kasse wurde abgeschlossen, aber zu diesem Abschluss gibt es keinen Auslesevorgang am Karten-Terminal.",
          "Entweder wurde das Terminal erst nach dem Ende des Exportzeitraums ausgelesen, oder der Kassenschnitt wurde vergessen. Im Händler-Report zeigt sich das als Sprung in den Uhrzeiten.",
        ],
        entscheidungen: [{ aktion: "counterpart", label: "Gegenwert erfassen" }, OHNE_TRINKGELD],
        warum:
          "Den Betrag dieses Abends im VR-Händler-Report nachsehen und eintragen. Dann stimmt das Trinkgeld.",
        sonst:
          "„Ohne Trinkgeld werten“, wenn an dieser Kasse gar nicht mit Karte gezahlt wurde: Garderobe, Storno, Abendkasse.",
      };

    case "MISSING_CLOSING":
      return {
        hergang: [
          "Das Karten-Terminal wurde ausgelesen, aber die Kasse dazu wurde nie abgeschlossen.",
          "Ohne Kassenabschluss lässt sich nicht sagen, welcher Teil davon Umsatz und welcher Trinkgeld war.",
        ],
        entscheidungen: [OHNE_TRINKGELD, { aktion: "counterpart", label: "Kassenumsatz eintragen" }],
        warum:
          "Ohne Gegenstück ist kein Trinkgeld nachweisbar. Der Umsatz bleibt erhalten, nur das Trinkgeld wird 0.",
        sonst: "„Kassenumsatz eintragen“, wenn der Kassenabschluss nachträglich auftaucht.",
      };

    case "SPLIT_REPORT":
    case "SPLIT_CLOSING": {
      const tage = geteilt?.beteiligte.map((b) => shortDate(b.businessDate)) ?? [];
      const letzter = tage.length > 0 ? tage[tage.length - 1] : null;
      const uebersprungen = tage.slice(0, -1).join(" und ");
      const anzahl = tage.length === 2 ? "beide" : `alle ${tage.length}`;
      const hergang =
        leit === "SPLIT_REPORT"
          ? [
              uebersprungen && letzter
                ? `Am ${uebersprungen} hat niemand das Karten-Terminal ausgelesen. Der Auslesevorgang am ${letzter} hat deshalb ${anzahl} Abende auf einmal erfasst.`
                : "Ein Auslesevorgang am Karten-Terminal deckt mehrere Abende ab, weil das Terminal zwischendurch nicht ausgelesen wurde.",
              "Das Terminal weiß nicht mehr, welche Zahlung zu welchem Abend gehörte. Die App verteilt deshalb im Verhältnis der Umsätze. Das ist eine Annahme, keine abgelesene Zahl.",
            ]
          : [
              uebersprungen && letzter
                ? `Am ${uebersprungen} wurde die Kasse nicht abgeschlossen. Der Abschluss am ${letzter} deckt deshalb ${anzahl} Abende auf einmal ab.`
                : "Ein Kassenabschluss deckt mehrere Abende ab, weil die Kasse zwischendurch nicht abgeschlossen wurde.",
              "Der Kartenumsatz beider Abende steckt in einer Zahl. Die App verteilt ihn im Verhältnis der Terminal-Beträge. Abgelesen ist diese Aufteilung nirgends, sie ist angenommen.",
            ];
      return {
        hergang,
        entscheidungen: [
          { aktion: "confirm", label: "Aufteilung übernehmen" },
          { aktion: "amounts", label: "Echte Werte eintragen" },
        ],
        warum:
          "Die Gesamtsumme stimmt, und ohne bessere Information ist die anteilige Verteilung die faire Annahme.",
        sonst:
          "„Echte Werte eintragen“ nur, wenn jemand die Tageswerte kennt, etwa aus dem VR-Portal; dann für jeden Tag einzeln.",
      };
    }

    case "IMPLAUSIBLE_TIP":
      return {
        hergang: [
          "Das Trinkgeld ist für diesen Umsatz ungewöhnlich hoch. Das kann stimmen, etwa bei einer großen Rechnung mit großzügigem Trinkgeld. Öfter heißt es aber, dass zwei Belege gepaart wurden, die nicht zusammengehören.",
        ],
        // Gelber Hinweis: blockiert nicht, also kein Knopf.
        entscheidungen: [],
        warum: null,
        sonst:
          "Stimmt die Paarung nicht: die Werte in der Rechnung ändern oder unten einen anderen Tag wählen.",
      };

    case "LATE_CLOSING":
      return {
        hergang: [
          "Die Kasse wurde erst am nächsten Tag tagsüber abgeschlossen, nicht in der Nacht. Die App hat den Umsatz deshalb dem Vorabend zugeordnet, an dem er entstanden ist.",
        ],
        entscheidungen: [],
        warum: null,
        sonst: "Gehört der Umsatz doch zum Tag des Abschlusses: unten „anderer Tag“.",
      };

    case "LATE_REPORT":
      return {
        hergang: [
          "Zwischen Kassenabschluss und dem Auslesen des Karten-Terminals liegen mehrere Stunden. Normal sind wenige Minuten.",
          "Die Zuordnung stimmt trotzdem meist; es hat nur jemand später ausgelesen.",
        ],
        entscheidungen: [],
        warum: null,
        sonst: "Passen die Beträge nicht zum Abend, stimmt die Paarung nicht: die Werte in der Rechnung ändern.",
      };

    case "COUNTERPART_IN_SIBLING":
      return {
        hergang: [
          "Die Kasse wurde an diesem Abend mehrfach abgeschlossen, das Terminal aber nur einmal ausgelesen. Für sich genommen sieht dieser Abschluss unvollständig aus.",
          "Der fehlende Gegenwert steckt in einem der anderen Abschlüsse desselben Abends. Für den Tag zusammengerechnet geht es auf.",
        ],
        entscheidungen: [],
        warum: null,
        sonst: null,
      };

    default:
      return { hergang: [], entscheidungen: [], warum: null, sonst: null };
  }
}
